import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown2, SearchNormal1 } from "iconsax-reactjs";
import "./Select.css";

// Lists longer than this get a filter field at the top of the menu.
const SEARCH_AFTER = 8;
// Space between the field and its menu, and between the menu and the window edge.
const GAP = 4;
const EDGE = 8;
const MAX_HEIGHT = 320;
// Letters typed within this window build one search ("vi" finds "Visit").
const TYPEAHEAD_MS = 600;

const isCharKey = (e) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
const coarsePointer = () => !!window.matchMedia?.("(pointer: coarse)")?.matches;
const matchesQuery = (option, query) =>
  !query || `${option.label} ${option.detail || ""}`.toLowerCase().includes(query);

function CheckIcon() {
  return (
    <svg className="gs-select-check" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/**
 * The portal's dropdown, used in place of the browser's select so every list
 * looks the same on every OS. A button shows the chosen option and opens a
 * listbox under it, or above it when the window has no room below. The
 * listbox renders into <body>, so a table, card or modal that clips its
 * overflow cannot cut it off.
 *
 * Keys follow a native select: arrows, Home, End and typed letters move
 * through the options, Enter or Space picks one, Escape and Tab close.
 * onChange receives the picked option's value, not an event.
 *
 * options: [{ value, label, detail?, hint?, disabled? }]. detail is a second
 * line (a phone number, a locality); hint sits at the right ("Current").
 */
export default function Select({
  id,
  value,
  onChange,
  options,
  placeholder = "Choose one",
  ariaLabel,
  disabled = false,
  required = false,
  invalid = false,
  className = "",
  searchable = options.length > SEARCH_AFTER,
  searchPlaceholder = "Search",
}) {
  const autoId = useId();
  const triggerId = id || `${autoId}-field`;
  const listId = `${autoId}-list`;
  const optionId = (index) => `${autoId}-option-${index}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const typed = useRef({ text: "", at: 0 });
  const revealActive = useRef(false);
  const side = useRef(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  // Indices into options: the rows on show, and the ones that can be picked.
  const needle = query.trim().toLowerCase();
  const visible = [];
  options.forEach((o, i) => {
    if (matchesQuery(o, needle)) visible.push(i);
  });
  const enabled = visible.filter((i) => !options[i].disabled);

  const highlight = (index) => {
    revealActive.current = true;
    setActive(index ?? -1);
  };

  const openMenu = (index) => {
    if (disabled) return;
    const first = options.findIndex((o) => !o.disabled);
    highlight(index ?? (selected && !selected.disabled ? selectedIndex : first));
    setQuery("");
    setOpen(true);
  };

  const close = useCallback((refocus = false) => {
    setOpen(false);
    setQuery("");
    if (refocus) triggerRef.current?.focus();
  }, []);

  const choose = (index) => {
    const option = options[index];
    if (!option || option.disabled) return;
    close(true);
    if (option.value !== value) onChange(option.value);
  };

  const step = (delta) => {
    if (!enabled.length) return;
    const at = enabled.indexOf(active);
    const next = at < 0
      ? (delta > 0 ? 0 : enabled.length - 1)
      : Math.min(enabled.length - 1, Math.max(0, at + delta));
    highlight(enabled[next]);
  };

  // The option a typed letter lands on. Pressing one letter again moves to
  // the next option starting with it; typing on narrows the match.
  const typedMatch = (char) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at < TYPEAHEAD_MS ? t.text + char.toLowerCase() : char.toLowerCase();
    t.at = now;
    const repeat = [...t.text].every((c) => c === t.text[0]);
    const prefix = repeat ? t.text[0] : t.text;
    const start = Math.max(0, enabled.indexOf(active) + (repeat ? 1 : 0));
    for (let k = 0; k < enabled.length; k += 1) {
      const i = enabled[(start + k) % enabled.length];
      if (String(options[i].label).toLowerCase().startsWith(prefix)) return i;
    }
    return -1;
  };

  const onKeyDown = (e) => {
    const inSearch = e.currentTarget === searchRef.current;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu();
      } else if (e.key === "Home" || e.key === "End") {
        e.preventDefault();
        openMenu(e.key === "Home" ? enabled[0] : enabled[enabled.length - 1]);
      } else if (isCharKey(e)) {
        const match = typedMatch(e.key);
        openMenu(match >= 0 ? match : undefined);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        step(e.key === "ArrowDown" ? 1 : -1);
        break;
      case "PageDown":
      case "PageUp":
        e.preventDefault();
        step(e.key === "PageDown" ? 10 : -10);
        break;
      case "Home":
      case "End":
        if (inSearch || !enabled.length) return;
        e.preventDefault();
        highlight(e.key === "Home" ? enabled[0] : enabled[enabled.length - 1]);
        break;
      case "Enter":
        e.preventDefault();
        if (visible.includes(active)) choose(active);
        break;
      case "Escape":
        // Stops here, so the modal around the field stays open.
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case "Tab":
        // Back to the field first, so Tab moves on from there rather than
        // from the menu at the end of the page.
        close(true);
        break;
      default: {
        if (inSearch || !isCharKey(e)) return;
        e.preventDefault();
        // Space picks, unless it falls inside a typed search ("visit s").
        if (e.key === " " && Date.now() - typed.current.at >= TYPEAHEAD_MS) {
          if (visible.includes(active)) choose(active);
          return;
        }
        const match = typedMatch(e.key);
        if (match >= 0) highlight(match);
      }
    }
  };

  // Focus leaving for anywhere but the menu closes it. Focus moving from the
  // filter back to the field is that field's click, which closes it anyway.
  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (next && (next === triggerRef.current || menuRef.current?.contains(next))) return;
    close();
  };

  const onSearch = (e) => {
    const text = e.target.value;
    setQuery(text);
    const q = text.trim().toLowerCase();
    highlight(options.findIndex((o) => !o.disabled && matchesQuery(o, q)));
  };

  useEffect(() => {
    if (disabled && open) close();
  }, [disabled, open, close]);

  // Focus goes to the filter when there is one (not on touch screens, where it
  // would raise the keyboard over the list), else stays on the field. Safari
  // does not focus a clicked button, so the field is focused here.
  useEffect(() => {
    if (!open) return;
    if (searchable && !coarsePointer()) searchRef.current?.focus();
    else triggerRef.current?.focus();
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  // Place the menu against the field: below it, or above when that side has
  // more room. The side holds while it stays open, so filtering never makes it
  // jump, and it follows the field when the page or a modal scrolls.
  useLayoutEffect(() => {
    if (!open) {
      side.current = null;
      return undefined;
    }
    const place = (event) => {
      const field = triggerRef.current;
      const menu = menuRef.current;
      if (!field || !menu) return;
      if (event?.type === "scroll" && menu.contains(event.target)) return;
      const box = field.getBoundingClientRect();
      const viewWidth = document.documentElement.clientWidth;
      const viewHeight = window.innerHeight;
      menu.style.minWidth = `${box.width}px`;
      menu.style.maxHeight = "none";
      const natural = Math.min(menu.offsetHeight, MAX_HEIGHT);
      const below = viewHeight - box.bottom - GAP - EDGE;
      const above = box.top - GAP - EDGE;
      if (!side.current) side.current = natural > below && above > below ? "top" : "bottom";
      const up = side.current === "top";
      const room = Math.max(0, Math.min(MAX_HEIGHT, up ? above : below));
      const width = menu.offsetWidth;
      let left = box.left;
      if (left + width > viewWidth - EDGE) left = Math.max(EDGE, box.right - width);
      menu.style.maxHeight = `${room}px`;
      menu.style.top = `${up ? box.top - GAP - Math.min(natural, room) : box.bottom + GAP}px`;
      menu.style.left = `${left}px`;
      menu.dataset.side = side.current;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, visible.length]);

  // Keep the highlighted option in view after a key moves it, but not on
  // hover, which would scroll the list under the pointer.
  useLayoutEffect(() => {
    if (!open || !revealActive.current) return;
    revealActive.current = false;
    const list = listRef.current;
    const row = list?.children[visible.indexOf(active)];
    if (!row) return;
    if (row.offsetTop < list.scrollTop) {
      list.scrollTop = row.offsetTop - GAP;
    } else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight + GAP;
    }
  });

  // Pressing in the menu keeps focus on the field (or the filter), and its
  // clicks stop here: the menu is its own layer, not part of the row or card
  // underneath it.
  const menu = (
    <div
      ref={menuRef}
      className={`gs-select-menu${searchable ? " is-searchable" : ""}`}
      onMouseDown={(e) => { if (e.target !== searchRef.current) e.preventDefault(); }}
      onClick={(e) => e.stopPropagation()}
    >
      {searchable && (
        <div className="gs-select-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            className="gs-select-search-input"
            role="combobox"
            aria-label={searchPlaceholder}
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? optionId(active) : undefined}
            placeholder={searchPlaceholder}
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={onSearch}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
        </div>
      )}
      <ul ref={listRef} id={listId} className="gs-select-list" role="listbox" aria-labelledby={triggerId}>
        {visible.map((i) => {
          const option = options[i];
          return (
            <li
              key={option.value}
              id={optionId(i)}
              className={`gs-select-option${i === active ? " is-active" : ""}`}
              role="option"
              aria-selected={i === selectedIndex}
              aria-disabled={option.disabled || undefined}
              onPointerMove={() => { if (!option.disabled && i !== active) setActive(i); }}
              onClick={() => choose(i)}
            >
              <CheckIcon />
              <span className="gs-select-option-text">
                <span className="gs-select-option-label">{option.label}</span>
                {option.detail && <>{" "}<span className="gs-select-option-detail">{option.detail}</span></>}
              </span>
              {option.hint && <>{" "}<span className="gs-select-option-hint">{option.hint}</span></>}
            </li>
          );
        })}
      </ul>
      {visible.length === 0 && <p className="gs-select-empty">No matches for “{query.trim()}”</p>}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        className={`select gs-select${className ? ` ${className}` : ""}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onKeyDown}
        // Firefox clicks a button on Space's keyup even after keydown is handled.
        onKeyUp={(e) => { if (e.key === " ") e.preventDefault(); }}
        onBlur={onBlur}
      >
        <span className={`gs-select-value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ArrowDown2 className="gs-select-chevron" size={16} variant="Linear" aria-hidden="true" />
      </button>
      {open && createPortal(menu, document.body)}
    </>
  );
}
